import { supabase } from '../lib/supabase';

/**
 * Resolve asset paths to real URLs.
 *
 * Handles:
 *  - Private bucket: "assets"     → signed URL
 *  - Public bucket:  "preview_assets" → public URL
 *  - Dev/local fallback: /public/assets/...
 *  - Removes incorrect leading prefixes ("assets/", "/assets/") before querying Supabase
 *  - Always logs Supabase failures but gracefully falls back
 *  - Returns fallback cube for invalid/empty paths
 */
const fallbackUrl = `${import.meta.env.BASE_URL || '/'}assets/fallback_cube.glb`;
const knownBuckets = new Set(['assets', 'preview_assets', 'profiles']);
const debugLog = (..._args: unknown[]) => {};
const warnLog = (..._args: unknown[]) => {};
const errorLog = (..._args: unknown[]) => {};

export async function resolveAssetUrl(pathOrUrl: string): Promise<string> {
  debugLog('[resolveAssetUrl] Called with:', pathOrUrl);

  if (!pathOrUrl || pathOrUrl.length < 3 || typeof pathOrUrl !== 'string') {
    warnLog('resolveAssetUrl: Invalid path provided, using fallback cube:', pathOrUrl);
    return fallbackUrl;
  }

  const tryCreateSignedUrl = async (bucket: string, objectKey: string) => {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(objectKey, 3600);
      if (!error && data?.signedUrl) {
        debugLog(`[resolveAssetUrl] Got signed URL from ${bucket}:`, data.signedUrl);
        return data.signedUrl;
      }
    } catch (err) {
      warnLog(`[resolveAssetUrl] ${bucket} signed URL failed`, err);
    }
    return undefined;
  };

  const tryGetPublicUrl = (bucket: string, objectKey: string) => {
    try {
      const { data } = supabase.storage.from(bucket).getPublicUrl(objectKey);
      return data?.publicUrl;
    } catch (err) {
      warnLog(`[resolveAssetUrl] ${bucket} lookup failed`, err);
    }
    return undefined;
  };

  // If already a full URL, return as-is (or re-sign if it's an assets signed URL)
  if (/^https?:\/\//i.test(pathOrUrl)) {
    const previewAssetsMatch = pathOrUrl.match(/\/storage\/v1\/object\/public\/preview_assets\/(.+?)(\?|$)/i);
    if (previewAssetsMatch?.[1]) {
      return pathOrUrl;
    }

    const signedAssetsMatch = pathOrUrl.match(/\/storage\/v1\/object\/sign\/assets\/(.+?)(\?|$)/i);
    if (signedAssetsMatch?.[1]) {
      const objectKey = decodeURIComponent(signedAssetsMatch[1]);
      const refreshed = await tryCreateSignedUrl('assets', objectKey);
      if (refreshed) return refreshed;
    }
    const publicAssetsMatch = pathOrUrl.match(/\/storage\/v1\/object\/public\/assets\/(.+?)(\?|$)/i);
    if (publicAssetsMatch?.[1]) {
      const objectKey = decodeURIComponent(publicAssetsMatch[1]);
      const previewUrl = tryGetPublicUrl('preview_assets', objectKey);
      if (previewUrl) return previewUrl;
      const refreshed = await tryCreateSignedUrl('assets', objectKey);
      if (refreshed) return refreshed;
    }
    // Normalize any unknown public bucket URLs to preview_assets or local static assets.
    const publicBucketMatch = pathOrUrl.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+?)(\?|$)/i);
    if (publicBucketMatch?.[1] && publicBucketMatch?.[2]) {
      const bucket = publicBucketMatch[1];
      const objectKey = decodeURIComponent(publicBucketMatch[2]);

      if (!knownBuckets.has(bucket)) {
        const lowResMatch = objectKey.match(/^(accessory|clothing|pet)\/([a-f0-9-]{36})\/model_low\.glb$/i);
        if (lowResMatch) {
          const category = lowResMatch[1];
          const assetId = lowResMatch[2];
          return `${import.meta.env.BASE_URL || '/'}assets/${category}/${assetId}/model.glb`;
        }

        const previewUrl = tryGetPublicUrl('preview_assets', objectKey);
        if (previewUrl) return previewUrl;

        return pathOrUrl.replace(
          `/storage/v1/object/public/${bucket}/`,
          '/storage/v1/object/public/preview_assets/'
        );
      }
    }
    debugLog('[resolveAssetUrl] Already a full URL:', pathOrUrl);
    return pathOrUrl;
  }

  // Normalize leading slashes and keep a copy for logging
  let path = pathOrUrl.replace(/^\/+/, '');
  const original = path;
  debugLog('[resolveAssetUrl] Normalized path:', path);

  if (!path || path.length < 3) {
    warnLog('resolveAssetUrl: Path too short after normalization, using fallback:', original);
    return fallbackUrl;
  }

  // Handle "assets/preview_assets/..." specially to avoid misrouting
  if (path.startsWith('assets/preview_assets/')) {
    const objectKey = path.replace(/^assets\//, '').replace(/^preview_assets\//, '');
    const url = tryGetPublicUrl('preview_assets', objectKey);
    if (url) return url;
    debugLog('[resolveAssetUrl] Falling back to static /assets backup for preview_assets key:', objectKey);
    return `/assets/${objectKey}`;
  }

  // Paths in the DB often look like "assets/<category>/<id>/model.glb". Try the
  // Supabase buckets first, then fall back to the static /public/assets backup.
  if (path.startsWith('assets/')) {
    const objectKey = path.replace(/^assets\//, '');
    const previewUrl = tryGetPublicUrl('preview_assets', objectKey);
    if (previewUrl) {
      return previewUrl;
    }

    const signedUrl = await tryCreateSignedUrl('assets', objectKey);
    if (signedUrl) {
      return signedUrl;
    }

    debugLog('[resolveAssetUrl] Falling back to static /assets backup for:', objectKey);
    return `/${path}`;
  }

  // Existing local category paths (without the assets/ prefix) are served from /public/assets
  if (/^(accessory|clothing|pet)\//.test(path)) {
    const assetPath = `assets/${path}`;
    debugLog('[resolveAssetUrl] Treating category path as static asset:', assetPath);
    return `/${assetPath}`;
  }

  // If the path begins with an unknown bucket-like prefix, avoid returning dead URLs.
  const firstSlash = path.indexOf('/');
  if (firstSlash > 0) {
    const bucketCandidate = path.slice(0, firstSlash);
    if (!knownBuckets.has(bucketCandidate)) {
      const remainder = path.slice(firstSlash + 1);
      if (/^(accessory|clothing|pet)\//.test(remainder)) {
        const localKey = remainder.replace(/model_low\.glb$/i, 'model.glb');
        debugLog('[resolveAssetUrl] Normalizing unknown bucket path to static asset:', localKey);
        return `${import.meta.env.BASE_URL || '/'}assets/${localKey}`;
      }
    }
  }

  // Check if this is preview_assets bucket (PUBLIC - for preview GLBs and thumbnails)
  if (path.startsWith('preview_assets/')) {
    const objectKey = path.replace(/^preview_assets\//, '');
    debugLog('[resolveAssetUrl] Attempting to get public URL for preview_assets:', objectKey);

    const url = tryGetPublicUrl('preview_assets', objectKey);
    if (url) return url;

    warnLog('resolveAssetUrl: preview_assets public URL failed', {
      original,
      objectKey,
    });
    
    return fallbackUrl;
  }

  // Path without bucket prefix - could be preview GLB
  // Check if this looks like an asset ID path (UUID/filename pattern)
  if (/^[a-f0-9-]{36}\//i.test(path)) {
    debugLog('[resolveAssetUrl] Looks like asset ID path, checking preview_assets first:', path);
    
    const previewUrl = tryGetPublicUrl('preview_assets', path);

    if (previewUrl) {
      return previewUrl;
    }
  }

  // Check if this is generated/ or user-upload/ prefix (could be in preview_assets public bucket)
  if (path.startsWith('generated/') || path.startsWith('user-upload/')) {
    debugLog('[resolveAssetUrl] Checking preview_assets for generated/user-upload path:', path);
    
    const previewUrl = tryGetPublicUrl('preview_assets', path);
    if (previewUrl) {
      return previewUrl;
    }

    // Fallback to private assets bucket with signed URL
    debugLog('[resolveAssetUrl] Attempting to get signed URL for assets bucket:', path);

    try {
      const { data, error } = await supabase.storage
        .from('assets')
        .createSignedUrl(path, 3600); // 1 hour expiry

      if (error) {
        errorLog('resolveAssetUrl: Failed to create signed URL for assets bucket:', error);
        return fallbackUrl;
      }

      if (data?.signedUrl) {
        debugLog('[resolveAssetUrl] Got signed URL:', data.signedUrl);
        return data.signedUrl;
      }

      warnLog('resolveAssetUrl: No signed URL returned for assets bucket');
      return fallbackUrl;
    } catch (err) {
      errorLog('resolveAssetUrl: assets bucket signed URL exception', err);
      return fallbackUrl;
    };
  };

  warnLog('resolveAssetUrl: No matching path handling, using fallback:', original);
  return fallbackUrl;
}
